import React, { useCallback, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import {
  useGetSavedThreadsQuery,
  useToggleSaveThreadMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useUserThreadsList } from '@/hooks/residents/corkBoard/useUserThreadsList';
import { useCorkBoardMutation } from '@/hooks/residents/corkBoard/useCorkBoardMutation';
import { useThreadActions } from '@/hooks/residents/corkBoard/useThreadActions';
import { useToast } from '@/contexts/admin/ToastContext';
import { NewsfeedUserListScreen } from '@/components/residents/redesign';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

type SavedThreadsNavigationProp = StackNavigationProp<RootStackParamList>;

export const SavedThreadsScreen: React.FC = () => {
  const navigation = useNavigation<SavedThreadsNavigationProp>();
  const { showSuccess, showError } = useToast();
  const [toggleSaveTrigger] = useToggleSaveThreadMutation();

  const { execute: executeToggleSave } = useCorkBoardMutation<string, any>(
    toggleSaveTrigger, { errorFallback: 'Failed to update bookmark.' },
  );

  const [unsavedIds, setUnsavedIds] = useState<Set<string>>(new Set());

  const {
    threads,
    isInitialLoad,
    isRefreshing,
    isLoadingMore,
    isError,
    handleRefresh,
    handleEndReached,
  } = useUserThreadsList({ queryHook: useGetSavedThreadsQuery, variant: 'saved' });

  const {
    handleEditThread,
    openDeleteConfirm,
    openReportModal,
    threadActionModals,
  } = useThreadActions();

  const visibleThreads = useMemo(
    () => unsavedIds.size > 0
      ? threads.filter((t) => !unsavedIds.has(String(t.id)))
      : threads,
    [threads, unsavedIds],
  );

  const handleThreadPress = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('ThreadDetail', { threadId: thread.id });
  }, [navigation]);

  const handleSaveThread = useCallback(async (thread: CorkBoardThreadSummary) => {
    const result = await executeToggleSave(thread.id, { silent: true });
    if (result.success) {
      const saved = result.data?.saved ?? !thread.isSaved;
      if (!saved) {
        setUnsavedIds((prev) => new Set(prev).add(String(thread.id)));
      }
      showSuccess(saved ? 'Thread saved' : 'Thread unsaved');
    } else {
      showError('Failed to save thread');
    }
  }, [executeToggleSave, showSuccess, showError]);

  const handleRefreshAndClear = useCallback(() => {
    setUnsavedIds(new Set());
    handleRefresh();
  }, [handleRefresh]);

  const handleBrowseFeed = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('CommunityBoard');
    }
  }, [navigation]);

  return (
    <>
      <NewsfeedUserListScreen
        title="Saved"
        subtitle="Posts you've bookmarked"
        screenName="SavedThreads"
        threads={visibleThreads}
        isInitialLoad={isInitialLoad}
        isRefreshing={isRefreshing}
        isLoadingMore={isLoadingMore}
        isError={isError}
        onRefresh={handleRefreshAndClear}
        onEndReached={handleEndReached}
        onThreadPress={handleThreadPress}
        onBack={handleBrowseFeed}
        emptyStateVariant="savedThreads"
        onBrowseFeed={handleBrowseFeed}
        onSave={handleSaveThread}
        onEdit={handleEditThread}
        onDelete={openDeleteConfirm}
        onReport={openReportModal}
      />
      {threadActionModals}
    </>
  );
};
